// Proactive "bind your email" prompt. Mounted once at the app root.
//
// Policy (decided with product):
//   - Audience: only "valuable" users — ENTITY accounts or accounts with a
//     positive balance (a proxy for "has topped up"). Brand-new virtual
//     accounts are not nagged. Users whose balance hit zero can't be detected
//     from the frontend; that's acceptable since the account menu always
//     offers the binding entry.
//   - Frequency: once per browser session. We mark sessionStorage the first
//     time we show it, so a page refresh in the same tab won't re-prompt.
//   - Strength: dismissable modal ("Maybe later" / "Bind now"), never blocking.

import React from 'react';
import { XShell } from '../shell.jsx';
import { useT } from '../i18n.jsx';
import { Dialog, DialogHeader, DialogBody } from '../ui/index.js';
import { EmailVerifyModal } from './EmailVerifyModal.jsx';

const dismissKey = (handle) => `xapi.emailReminderShown:${handle || 'anon'}`;

export const EmailReminder = () => {
  const { user, authed, refreshMe } = XShell.useAuth();
  const { t } = useT();
  const [open, setOpen] = React.useState(false);
  const [verifyOpen, setVerifyOpen] = React.useState(false);

  const handle = user && user.handle;
  const hasEmail = !!(user && user.email);
  const isEntity = user && user.accountType === 'ENTITY';
  const hasBalance = !!(user && Number(user.balance) > 0);
  const shouldRemind = authed && !hasEmail && (isEntity || hasBalance);

  React.useEffect(() => {
    if (!shouldRemind) { setOpen(false); return; }
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(dismissKey(handle)) === '1'; } catch {}
    if (alreadyShown) return;
    // Mark as shown immediately so a refresh in the same session won't re-prompt
    // regardless of how the user closes it ("once per session").
    try { sessionStorage.setItem(dismissKey(handle), '1'); } catch {}
    setOpen(true);
  }, [shouldRemind, handle]);

  if (verifyOpen) {
    return (
      <EmailVerifyModal
        onClose={() => setVerifyOpen(false)}
        onDone={async () => {
          setVerifyOpen(false);
          setOpen(false);
          try { await refreshMe(); } catch {}
        }}
      />
    );
  }

  if (!open) return null;

  return (
    <Dialog open={true} onClose={() => setOpen(false)} maxWidth={420}>
      <DialogHeader title={t('email.reminder.title')} onClose={() => setOpen(false)} />
      <DialogBody>
        <p style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.7, margin: '0 0 18px' }}>
          {t('email.reminder.body')}
        </p>
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" onClick={() => setOpen(false)}>{t('email.reminder.later')}</button>
          <button className="btn btn-primary" onClick={() => { setOpen(false); setVerifyOpen(true); }}>
            {t('email.reminder.bind')}
          </button>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default EmailReminder;
